import {
  SEARCH_SORT_VALUES,
  SORT_URL_PARAM,
  VALID_SEARCH_URL_PARAMS,
} from "@/components/Search/constants";

/**
 * One public URL key for each form-state key. Aliases such as `attributes`
 * and `subject` share `subjects`, so the write uses a single canonical param.
 */
export const FORM_STATE_TO_URL_PARAM: Readonly<Record<string, string>> = {
  query: "q",
  type: "tab",
  subjects: "subject",
  skills: "skills.skill",
  productType: "productType",
  "aiLanguages.translationLanguages": "ai_languages.translation_languages",
  partner: "partner",
  language: "language",
  level: "level",
  availability: "availability",
  sort: "sort",
};

export const QUERY_URL_DEBOUNCE_MS = 300;

const approvedUrlKeys = new Set<string>(VALID_SEARCH_URL_PARAMS);
const supportedSortValues = new Set<string>(SEARCH_SORT_VALUES);

export const formStateSignature = (
  formState: Readonly<Record<string, string>>,
): string =>
  JSON.stringify(
    Object.entries(formState)
      .filter(([, value]) => value !== "")
      .sort(([left], [right]) => left.localeCompare(right)),
  );

/**
 * Replaces the approved search params and leaves every other param in place.
 * Empty form-state values remove their URL param.
 */
export const applyFormStateToSearch = (
  formState: Readonly<Record<string, string>>,
  currentSearch: string,
): string => {
  const params = new URLSearchParams(currentSearch);
  approvedUrlKeys.forEach((key) => params.delete(key));

  Object.entries(formState).forEach(([key, value]) => {
    if (value === "") return;
    const urlKey = FORM_STATE_TO_URL_PARAM[key];
    if (!urlKey || !approvedUrlKeys.has(urlKey)) return;
    if (urlKey === SORT_URL_PARAM && !supportedSortValues.has(value)) return;
    // Repeated params match the legacy search URL. Comma-joined form state
    // is the same join `buildInitialFormState` uses when it reads them back.
    value.split(",").forEach((part) => {
      if (part !== "") params.append(urlKey, part);
    });
  });

  const query = params.toString();
  return query ? `?${query}` : "";
};

export const replaceSearchUrl = (
  formState: Readonly<Record<string, string>>,
): string => {
  const nextSearch = applyFormStateToSearch(formState, window.location.search);
  const { hash, pathname, search } = window.location;
  const nextUrl = `${pathname}${nextSearch}${hash}`;
  const currentUrl = `${pathname}${search}${hash}`;
  if (currentUrl !== nextUrl) {
    window.history.replaceState(null, "", nextUrl);
  }
  return formStateSignature(formState);
};
